import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, ExternalLink } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRODUCTS, INDUSTRIES } from "@/data/content";
import { ProductIcon } from "@/lib/icons";
import { PrimaryLink, GhostLink } from "@/components/shared";

const ProductDetail = () => {
  const { slug } = useParams();
  const { t, pick } = useLanguage();
  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return <Navigate to="/products" replace />;

  const features = pick(product.features);
  const idx = PRODUCTS.indexOf(product);
  const next = PRODUCTS[(idx + 1) % PRODUCTS.length];
  const usedBy = INDUSTRIES.filter((i) => i.stack.includes(slug));

  return (
    <div data-testid={`product-page-${slug}`}>
      <section className="relative py-20 sm:py-28 px-5 sm:px-8 grid-bg overflow-hidden">
        <div className="absolute -top-32 left-1/3 w-[700px] h-[500px] glow-orb pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <Link to="/products" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-10 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t("common.backToProducts")}
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-8">
            <span className={`w-16 h-16 rounded-3xl bg-gradient-to-br ${product.accent.grad} flex items-center justify-center shadow-xl shrink-0`}>
              <ProductIcon name={product.icon} className="w-8 h-8 text-white" />
            </span>
            <div>
              <span className={`inline-block text-[10px] font-mono2 uppercase tracking-widest px-2.5 py-1 rounded-full ${product.accent.badge} mb-2`}>{product.short}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-heading tracking-tight text-white">{product.name}</h1>
              <p className={`text-sm font-mono2 uppercase tracking-[0.2em] ${product.accent.text} mt-2`}>{pick(product.tagline)}</p>
            </div>
          </div>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl mb-10">{pick(product.desc)}</p>
          <div className="flex flex-wrap gap-3">
            <PrimaryLink to="/signup" testId="product-start-free">{t("common.startFree")}</PrimaryLink>
            <GhostLink to="/contact" testId="product-book-demo">{t("common.bookDemo")}</GhostLink>
            {product.appUrl && (
              <a href={product.appUrl} target="_blank" rel="noopener noreferrer" data-testid="product-open-app" className="inline-flex items-center gap-2 rounded-full border border-emerald-500/50 text-emerald-300 font-semibold px-7 py-3.5 text-sm hover:bg-emerald-500/10 transition-colors">
                {t("common.openApp")} <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-5 sm:px-8 bg-[#05070B]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-semibold font-heading text-white mb-8">{t("common.features")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {features.map((f, i) => (
              <div key={f} data-testid={`feature-${i}`} className={`card-surface rounded-2xl px-4 py-3.5 flex items-center gap-2.5 text-sm text-slate-200 border border-slate-800/80 ${product.accent.border} transition-colors`}>
                <Check className={`w-4 h-4 ${product.accent.text} shrink-0`} /> {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {usedBy.length > 0 && (
        <section className="py-16 px-5 sm:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl sm:text-2xl font-semibold font-heading text-white mb-6">{t("nav.industries")}</h2>
            <div className="flex flex-wrap gap-2.5">
              {usedBy.map((i) => (
                <Link key={i.slug} to={`/industries/${i.slug}`} data-testid={`usedby-${i.slug}`} className="text-sm text-slate-300 bg-slate-800/70 border border-slate-700 hover:border-indigo-500/50 rounded-full px-4 py-2 transition-colors">
                  {pick(i.name)}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 px-5 sm:px-8 border-t border-slate-800/60">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-slate-400 text-sm">{t("home.flowTitle")}</p>
          <Link to={`/products/${next.slug}`} data-testid="next-product" className={`inline-flex items-center gap-3 text-lg font-heading font-bold ${next.accent.text} group`}>
            {next.name} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
